import { useEffect, useRef, useState } from 'react';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import { orderBy, limit, Timestamp } from 'firebase/firestore';
import type {
  PlayerChatDoc,
  SceneDoc,
  ScenePlayerPresenceDoc,
} from '@spirits/shared';
import { useCollection } from '../hooks/useCollection';
import { useDoc } from '../hooks/useDoc';
import { useGameStore } from '../store/useGameStore';
import { functions } from '../firebase';

const postPlayerChatCallable = httpsCallable<
  { storyId: string; text: string },
  { ok: boolean }
>(functions, 'postPlayerChat');

const MAX = 240;
const STALE_MS = 60_000;

function tsToMillis(ts: unknown): number {
  if (!ts) return 0;
  if (ts instanceof Timestamp) return ts.toMillis();
  if (typeof ts === 'object' && ts && 'seconds' in (ts as Record<string, unknown>)) {
    const t = ts as { seconds: number; nanoseconds?: number };
    return t.seconds * 1000 + Math.floor((t.nanoseconds ?? 0) / 1e6);
  }
  return 0;
}

function Halo() {
  return (
    <span className="halo-wrap soft" aria-hidden>
      <span className="halo-ring soft">
        <span className="halo-core soft" />
      </span>
    </span>
  );
}

export function PlayerChat({ sceneId }: { sceneId: string }) {
  const myUid = useGameStore((s) => s.user?.uid);

  // The chat is keyed by the story, not the scene — one shared history across
  // every scene of the storyline. Derive the storyId from the scene.
  const { data: scene } = useDoc<SceneDoc>(`scenes/${sceneId}`);
  const storyId = scene?.storyId ?? null;

  const { data: messages, loading } = useCollection<PlayerChatDoc>(
    storyId ? `stories/${storyId}/playerChat` : null,
    orderBy('at', 'desc'),
    limit(60),
  );
  const ordered = [...messages].reverse();

  // Presence stays scene-scoped: who is here right now, whatever bot they guide.
  const { data: players } = useCollection<ScenePlayerPresenceDoc>(
    `scenes/${sceneId}/players`,
  );
  const now = Date.now();
  const present = players
    .filter((p) => now - tsToMillis(p.lastSeenAt) < STALE_MS)
    .sort((a, b) => a.playerName.localeCompare(b.playerName));

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [ordered.length]);

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    const trimmed = text.trim();
    if (!trimmed || sending || !storyId) return;
    setSending(true);
    setError(null);
    try {
      await postPlayerChatCallable({ storyId, text: trimmed });
      setText('');
    } catch (e) {
      const fe = e as FunctionsError;
      setError(fe?.message ?? 'message refused');
    } finally {
      setSending(false);
    }
  }

  return (
    <aside className="hidden xl:flex w-72 flex-none flex-col border-l border-violet-900/40 bg-violet-950/15 min-h-0">
      <div className="flex-none px-3 py-2 border-b border-violet-900/40">
        <p className="text-[10px] uppercase tracking-widest text-violet-300/80">
          ✧ spirits' channel
        </p>
        <p className="text-[10px] text-slate-500 mt-0.5">
          coordinate across{' '}
          <span className="text-violet-200/80">{scene?.storyTitle ?? 'the story'}</span>
        </p>
      </div>

      <div className="flex-none px-3 py-2 border-b border-violet-900/40">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
          spirits present · {present.length}
        </p>
        {present.length === 0 ? (
          <p className="text-[11px] text-slate-600">no one yet</p>
        ) : (
          <ul className="flex flex-wrap gap-x-3 gap-y-1.5">
            {present.map((p) => (
              <li
                key={p.uid}
                className="flex items-center gap-1.5"
                title={`${p.playerName} — present`}
              >
                <Halo />
                <span
                  className={`truncate text-[11px] spirit-flicker ${
                    p.uid === myUid ? 'text-amber-300/90' : 'text-slate-400'
                  }`}
                >
                  {p.playerName}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-2 space-y-1 min-h-0 text-xs"
      >
        {loading && ordered.length === 0 && (
          <p className="text-slate-600 ">quiet…</p>
        )}
        {!loading && ordered.length === 0 && (
          <p className="text-slate-600 ">no one has spoken yet.</p>
        )}
        {ordered.map((m) => {
          const isMe = m.uid === myUid;
          return (
            <div key={m.id} className="leading-snug">
              <span
                className={` mr-1.5 ${
                  isMe ? 'text-amber-300/90' : 'text-violet-300/85'
                }`}
              >
                {m.playerName}
              </span>
              <span className="text-slate-300 break-words">{m.text}</span>
            </div>
          );
        })}
      </div>

      <form
        className="flex-none border-t border-violet-900/40 p-2 space-y-1"
        onSubmit={(e) => { e.preventDefault(); send(); }}
      >
        {error && <p className="text-[11px] text-rose-400">{error}</p>}
        <div className="flex gap-1.5">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={MAX}
            placeholder="message other spirits…"
            className="flex-1 min-w-0 rounded-md bg-slate-950/60 border border-violet-900/40 px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-violet-400/60"
          />
          <button
            type="submit"
            disabled={sending || !text.trim() || !storyId}
            className="rounded-md bg-violet-500/70 hover:bg-violet-400 text-slate-950 font-medium px-2.5 py-1.5 text-xs disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            send
          </button>
        </div>
      </form>
    </aside>
  );
}
